import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const DEFAULT_TZ = "UTC";

type TimezoneContextValue = {
  timezone: string;
};

const TimezoneContext = createContext<TimezoneContextValue>({ timezone: DEFAULT_TZ });

export function detectTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && tz.trim().length > 0 ? tz : DEFAULT_TZ;
  } catch {
    return DEFAULT_TZ;
  }
}

export function TimezoneProvider({ children }: { children: React.ReactNode }) {
  const [timezone, setTimezone] = useState<string>(() => detectTimezone());

  useEffect(() => {
    const refreshTimezone = () => setTimezone(detectTimezone());
    window.addEventListener("focus", refreshTimezone);
    return () => window.removeEventListener("focus", refreshTimezone);
  }, []);

  const value = useMemo(() => ({ timezone }), [timezone]);
  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
}

export function useTimezone(): string {
  return useContext(TimezoneContext).timezone;
}
